import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Calendar, Home, CreditCard, HardHat, Wrench, TrendingUp, FileText, Download, ArrowLeft } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { YieldDisclaimer } from "../components/YieldDisclaimer";
import { FullConsultationForm } from "../components/FullConsultationForm";
import { PROJECTS, LAST_UPDATED, GLOBAL_DISCLAIMER } from "../data/site";

export default function ProjectDetail() {
  const { id } = useParams();
  const project = PROJECTS.find((p) => p.id === id);
  if (!project) return <Navigate to="/projects" replace />;

  const rows = [
    { icon: MapPin, label: "Location", value: project.location },
    { icon: Home, label: "Developer", value: project.developer },
    { icon: TrendingUp, label: "Starting price", value: project.price },
    { icon: FileText, label: "Unit types", value: project.unitTypes },
    { icon: CreditCard, label: "Payment plan", value: project.paymentPlan },
    { icon: Calendar, label: "Handover date", value: project.handover },
    { icon: HardHat, label: "Construction status", value: project.constructionStatus },
    { icon: Wrench, label: "Service charge estimate", value: project.serviceChargeEstimate },
    { icon: TrendingUp, label: project.yieldLabel, value: project.yield },
  ];

  return (
    <PageShell>
      <section
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-12 border-b border-white/5 overflow-hidden"
        style={{ background: `linear-gradient(180deg, rgba(10,17,40,0.55) 0%, #0a1128 100%), url(${project.image}) center/cover` }}
      >
        <div className="max-w-6xl mx-auto">
          <Link to="/projects" className="inline-flex items-center gap-2 text-white/60 hover:text-[#c5a059] text-xs tracking-[0.25em] uppercase mb-8" data-testid="back-to-projects">
            <ArrowLeft size={14} /> All Projects
          </Link>
          <div className="text-[10px] tracking-[0.5em] text-[#c5a059] uppercase mb-4">{project.developer}</div>
          <h1 className="font-serif-display text-4xl md:text-6xl font-light mb-4">{project.name}</h1>
          <p className="text-white/70 italic">{project.tag} · {project.location}</p>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-8">
            <div className="glass rounded-2xl overflow-hidden">
              <div className="divide-y divide-white/5">
                {rows.map((r) => {
                  const Icon = r.icon;
                  return (
                    <div key={r.label} className="flex items-center justify-between px-6 py-4 gap-4">
                      <div className="flex items-center gap-3 text-white/70">
                        <Icon size={15} className="text-[#c5a059]" />
                        <span className="text-sm">{r.label}</span>
                      </div>
                      <div className="text-right font-medium text-sm text-white">{r.value}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <YieldDisclaimer />

            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#c5a059] mb-3">
                <FileText size={12} /> Downloads
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a href="#request-brochure" className="btn-outline-gold rounded-md py-3 px-4 text-[11px] tracking-[0.25em] flex items-center justify-center gap-2" data-testid="download-brochure">
                  <Download size={14} /> REQUEST BROCHURE
                </a>
                <a href="#request-floorplans" className="btn-outline-gold rounded-md py-3 px-4 text-[11px] tracking-[0.25em] flex items-center justify-center gap-2" data-testid="download-floorplans">
                  <Download size={14} /> FLOOR PLANS
                </a>
              </div>
              <p className="text-[10px] text-white/40 italic mt-4">
                Official brochures and floor plans are shared privately following your consultation request below.
              </p>
            </div>

            <div className="glass rounded-2xl overflow-hidden">
              <div className="px-6 py-4 text-[10px] tracking-[0.3em] uppercase text-[#c5a059] border-b border-white/5">Location</div>
              <iframe
                title={`${project.name} location`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(project.mapQuery)}&output=embed`}
                className="w-full h-80 border-0"
                loading="lazy"
                style={{ filter: "grayscale(0.3) contrast(1.05)" }}
              />
            </div>

            <div className="rounded-xl border border-white/10 p-5 bg-white/[0.03]" data-testid="project-disclaimer">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#c5a059] mb-2">Project Disclaimer</div>
              <p className="text-xs text-white/60 leading-relaxed">
                {GLOBAL_DISCLAIMER}
              </p>
            </div>

            <p className="text-[10px] tracking-[0.3em] uppercase text-white/40" data-testid="last-updated">
              Information last updated · {project.lastUpdated || LAST_UPDATED}
            </p>
          </div>

          <aside className="lg:col-span-5">
            <div className="glass-strong rounded-2xl p-6 md:p-8 sticky top-28" id="request-brochure">
              <div className="text-[10px] tracking-[0.4em] text-[#c5a059] uppercase mb-3">Request Details</div>
              <h3 className="font-serif-display text-2xl mb-4">Enquire about {project.name}</h3>
              <FullConsultationForm compact />
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}
